import { useCallback, useMemo } from "react";
import { useForm, useController } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTokenValidation } from "@/hooks";
import type * as yup from "yup";
import { TOKEN_AMOUNT_KEY, LOCK_DURATION_KEY, type TokenFormValues } from "@/types";

interface UseDisconnectedTokenFormProps {
    maxValue?: string;
    extendSchema?: yup.AnyObjectSchema;
    initialAmount?: number;
}

const reValidateMode = "onSubmit";

export function useDisconnectedTokenForm({ maxValue, extendSchema, initialAmount }: UseDisconnectedTokenFormProps) {
    const tokenSchema = useTokenValidation({ balance: maxValue ?? "0" });
    const finalSchema = useMemo(
        () => (extendSchema ? tokenSchema.concat(extendSchema) : tokenSchema),
        [tokenSchema, extendSchema],
    );

    const resolver = useMemo(() => yupResolver(finalSchema), [finalSchema]);

    const form = useForm<TokenFormValues>({
        resolver,
        mode: reValidateMode,
        reValidateMode,
    });

    const {
        field: amount,
        fieldState: { error: amountError },
    } = useController({
        name: TOKEN_AMOUNT_KEY,
        control: form.control,
        defaultValue: initialAmount ? String(initialAmount) : "",
    });

    const setMaxAmount = useCallback(() => {
        amount.onChange(maxValue);
    }, [amount, maxValue]);

    const resetAmount = useCallback(() => {
        amount.onChange("");
    }, [amount]);

    const isAmountOverBalance = useMemo(() => {
        const parsedAmount = Number(amount.value);
        const parsedBalance = Number(maxValue ?? "0");

        if (!Number.isFinite(parsedAmount) || !Number.isFinite(parsedBalance)) {
            return false;
        }

        return parsedAmount > parsedBalance;
    }, [amount.value, maxValue]);

    return {
        form,
        amount,
        amountError,
        isAmountOverBalance,
        setMaxAmount,
        resetAmount,
        TOKEN_AMOUNT_KEY,
        LOCK_DURATION_KEY,
    };
}
